/**
 * Feedback Service - API client for stakeholder feedback system
 * 
 * Implements the Continuous Stakeholder Feedback System from Employee Journey Section 2.
 */

import { apiClient } from '../apiClient'

// ==================== Types ====================

export type FeedbackRequestType = 'matrix' | 'upward_360' | 'employee_requested' | 'information_checkin' | 'project'

export type FeedbackRequestStatus = 
  | 'draft' 
  | 'pending_approval' 
  | 'active' 
  | 'in_progress' 
  | 'completed' 
  | 'cancelled' 
  | 'expired'

export type StakeholderResponseStatus = 
  | 'pending' 
  | 'in_progress' 
  | 'completed' 
  | 'declined' 
  | 'expired'

export type FeedbackCategory = 
  | 'campus_gm' 
  | 'project_manager' 
  | 'faculty_team' 
  | 'cross_functional' 
  | 'external' 
  | 'direct_report' 
  | 'campus_based_employee' 
  | 'peer' 
  | 'other'

export interface FeedbackQuestion {
  id: string
  text: string
  type: 'rating' | 'rating_scale' | 'text' | 'multiple_choice' | 'scale' | 'likert' | 'multi_select' | 'yes_no'
  required: boolean
  options?: string[]
  scale?: {
    min: number
    max: number
    min_label?: string
    max_label?: string
    labels?: Record<number, string>
  }
  config?: {
    min?: number
    max?: number
    min_label?: string
    max_label?: string
    options?: string[]
  }
  category?: string
  help_text?: string
  /**
   * Where the question came from, set on the requester's final list (Oct
   * 2026). A request whose questions carry it is answered exactly as stored;
   * older requests and campaigns have none and answer the template.
   */
  source?: 'template' | 'custom'
  /** Question bank id of a template question, so answers group with it. */
  bank_question_id?: string
}

/**
 * #29 Inferred relationship between the logged-in responder and the
 * requester, computed backend-side from the org tree.
 *   - DIRECT_REPORT / INDIRECT_REPORT: requester is below the responder
 *   - PEER: same level / no hierarchy link inside the org
 *   - EXTERNAL: no internal identity record for the requester
 */
export type FeedbackRelationship =
  | 'DIRECT_REPORT'
  | 'INDIRECT_REPORT'
  | 'PEER'
  | 'EXTERNAL'

/**
 * #28 Enriched requester brief attached to incoming stakeholder rows so
 * the inbox can show a human name + title instead of the raw personnel
 * code (e.g. "UAT-EMP-UAT033").
 */
export interface FeedbackRequester {
  employee_id: string
  display_name?: string | null
  job_title?: string | null
}

export interface FeedbackStakeholder {
  id?: string
  feedback_request_id: string
  stakeholder_user_id: string
  stakeholder_name: string
  stakeholder_role?: string
  stakeholder_email?: string
  category: FeedbackCategory
  response_status?: StakeholderResponseStatus
  response_id?: string
  anonymity_token?: string
  is_anonymous_submission?: boolean
  invitation_sent_at?: string
  response_submitted_at?: string
  created_at?: string
  // Optional nested request details when eagerly loaded
  feedback_request?: Partial<FeedbackRequest>
  // #28/#29 Enrichment on the pending/completed inbox endpoints.
  requester?: FeedbackRequester
  relationship?: FeedbackRelationship
}

export interface FeedbackRequest {
  id?: string
  request_type: FeedbackRequestType
  requested_by_user_id: string
  requested_by_user_name?: string  // #28 Populated on request detail (display name of requester)
  requested_by_job_title?: string  // #28 Populated on request detail (requester's job title)
  requested_for_user_id: string
  requested_for_user_name?: string  // Populated by anonymous endpoint to avoid auth call
  already_responded?: boolean  // True when stakeholder already submitted feedback
  title: string
  description?: string
  instructions?: string
  question_template_id?: string
  question_template?: any  // Template from question bank (if used)
  custom_questions?: FeedbackQuestion[]
  is_confidential: boolean
  is_anonymous: boolean
  ai_analysis_enabled: boolean
  integrate_to_evidence: boolean
  due_date?: string
  review_session_id?: string
  deliver_ai_report_to?: string
  status?: FeedbackRequestStatus
  stakeholders: FeedbackStakeholder[]
  total_stakeholders?: number
  responses_received?: number
  responses_pending?: number
  /**
   * When non-null, this request belongs to an annual upward feedback
   * campaign. Visibility rules differ — responses are respondent-only
   * and the inbox should label the entry as a campaign invitation.
   * See P1.5 visibility guard.
   */
  campaign_id?: string | null
  /**
   * The campaign's subject_relationship (line_manager /
   * campus_general_manager / organisation), populated on the detail
   * endpoint. Organisation-wide surveys (e.g. the Culture Survey) have no
   * subject — the response form drops the "About <person>" framing.
   */
  campaign_subject_relationship?: string | null
  created_at?: string
  updated_at?: string
}

/**
 * What the public feedback link answers with (Oct 2026): exactly what the
 * external response page reads. External stakeholders answer without signing
 * in, so it carries no stakeholders, requester, personnel numbers or approval
 * details.
 */
export interface AnonymousFeedbackRequest {
  id: string
  title: string
  instructions?: string
  is_anonymous: boolean
  is_confidential: boolean
  custom_questions?: FeedbackQuestion[]
  /** The template's questions only; read for requests without a final list. */
  question_template?: {
    template_questions?: {
      display_order: number
      is_required: boolean
      custom_question_text?: string
      custom_helper_text?: string
      question: {
        id: string
        question_text: string
        question_type: string
        helper_text?: string
        question_config?: Record<string, unknown>
      }
    }[]
  }
  /** The subject's name only; unknown when the lookup fails. */
  requested_for_user_name?: string
  already_responded: boolean
}

export interface QuestionResponse {
  question_id: string
  question_text: string
  response_type: string
  answer: any
  rating?: number
  comment?: string
}

export interface FeedbackResponse {
  id?: string
  feedback_request_id: string
  feedback_stakeholder_id: string
  submitted_by_user_id?: string
  is_anonymous: boolean
  response_data: QuestionResponse[]
  overall_rating?: number
  general_comments?: string
  submitted_at?: string
}

// ==================== #31 "Add to…" integration result shapes ====================

/** GET /responses/{id}/portfolio-status */
export interface FeedbackResponseIntegrationStatus {
  in_portfolio: boolean
  portfolio_evidence_id: string | null
  in_idp: boolean
  idp_strength_id: string | null
}

/** POST /responses/{id}/add-to-portfolio and /add-to-pl-observations */
export interface AddToPortfolioResult {
  already_added: boolean
  evidence_id: string
  title: string
}

/** POST /responses/{id}/add-to-idp */
export interface AddToIdpResult {
  already_added: boolean
  strength_id: string
  title: string
}

export interface FeedbackQuestionTemplate {
  id?: string
  template_name: string
  template_code: string
  description?: string
  category: string
  applicable_roles?: string[]
  questions: FeedbackQuestion[]
  usage_count?: number
  is_active: boolean
  is_default?: boolean
}

export interface FeedbackStatistics {
  requests_initiated: number
  feedback_received: number
  pending_responses: number
  completed_responses: number
}

// ==================== Feedback Request Operations ====================

export const feedbackService = {
  /**
   * Create a new feedback request
   */
  async createFeedbackRequest(data: Omit<FeedbackRequest, 'id' | 'status' | 'created_at' | 'updated_at'>): Promise<FeedbackRequest> {
    const response = await apiClient.post<FeedbackRequest>('/feedback/requests', data)
    return response.data
  },

  /**
   * List feedback requests for current user
   */
  async listFeedbackRequests(params?: {
    request_type?: FeedbackRequestType
    status?: FeedbackRequestStatus
    as_requester?: boolean
    as_subject?: boolean
  }): Promise<FeedbackRequest[]> {
    const response = await apiClient.get<FeedbackRequest[]>('/feedback/requests', { params })
    return response.data
  },

  /**
   * List feedback requests for a team member (manager access)
   */
  async listTeamMemberFeedbackRequests(
    userId: string,
    params?: {
      request_type?: FeedbackRequestType
      status?: FeedbackRequestStatus
    }
  ): Promise<FeedbackRequest[]> {
    const response = await apiClient.get<FeedbackRequest[]>(
      `/feedback/requests/team-member/${userId}`,
      { params }
    )
    return response.data
  },

  /**
   * Get feedback request details
   * @param userId - Personnel number for authorization check
   */
  async getFeedbackRequest(requestId: string, userId?: string): Promise<FeedbackRequest> {
    const params = userId ? { user_id: userId } : {}
    const response = await apiClient.get<FeedbackRequest>(`/feedback/requests/${requestId}`, { params })
    return response.data
  },

  /**
   * Update feedback request
   */
  async updateFeedbackRequest(
    requestId: string, 
    data: Partial<FeedbackRequest>
  ): Promise<FeedbackRequest> {
    const response = await apiClient.patch<FeedbackRequest>(`/feedback/requests/${requestId}`, data)
    return response.data
  },

  /**
   * Send feedback request to stakeholders
   */
  async sendFeedbackRequest(requestId: string): Promise<void> {
    await apiClient.post(`/feedback/requests/${requestId}/send`)
  },

  /**
   * Approve employee-requested feedback
   */
  async approveFeedbackRequest(
    requestId: string, 
    approved: boolean, 
    notes?: string
  ): Promise<void> {
    await apiClient.post(`/feedback/requests/${requestId}/approve`, {
      approved,
      approval_notes: notes
    })
  },

  // ==================== Stakeholder Management ====================

  /**
   * Add a stakeholder to an existing feedback request
   */
  async addStakeholder(
    requestId: string,
    stakeholder: {
      stakeholder_user_id?: string
      stakeholder_name: string
      stakeholder_email?: string
      stakeholder_role?: string
      category: FeedbackCategory
    }
  ): Promise<FeedbackStakeholder> {
    const response = await apiClient.post<FeedbackStakeholder>(
      `/feedback/requests/${requestId}/stakeholders`,
      stakeholder
    )
    return response.data
  },

  /**
   * Remove a stakeholder from a feedback request
   * Only works if stakeholder hasn't responded yet
   */
  async removeStakeholder(requestId: string, stakeholderId: string): Promise<void> {
    await apiClient.delete(`/feedback/requests/${requestId}/stakeholders/${stakeholderId}`)
  },

  /**
   * Delete (soft) a feedback request
   * Only works if no responses have been received
   */
  async deleteFeedbackRequest(requestId: string): Promise<void> {
    await apiClient.delete(`/feedback/requests/${requestId}`)
  },

  // ==================== Feedback Response Operations ====================

  /**
   * Submit feedback response
   */
  async submitFeedbackResponse(data: FeedbackResponse): Promise<FeedbackResponse> {
    const response = await apiClient.post<FeedbackResponse>('/feedback/responses', data)
    return response.data
  },

  /**
   * Get feedback request by anonymity token (for external stakeholders)
   */
  async getAnonymousFeedbackRequest(token: string): Promise<AnonymousFeedbackRequest> {
    const response = await apiClient.get<AnonymousFeedbackRequest>(`/feedback/anonymous/${token}`)
    return response.data
  },

  /**
   * Submit anonymous feedback using token
   */
  async submitAnonymousFeedback(data: {
    anonymity_token: string
    response_data: QuestionResponse[]
    overall_rating?: number
    general_comments?: string
  }): Promise<void> {
    await apiClient.post('/feedback/responses/anonymous', data)
  },

  /**
   * Get pending feedback responses for current user
   * @param userId - Personnel number to check pending feedback for
   */
  async getPendingResponses(userId?: string): Promise<FeedbackStakeholder[]> {
    const params = userId ? { user_id: userId } : {}
    const response = await apiClient.get<FeedbackStakeholder[]>('/feedback/stakeholders/pending', { params })
    return response.data
  },

  /**
   * Get completed feedback responses for current user
   * @param userId - Personnel number to check completed feedback for
   */
  async getCompletedResponses(userId?: string): Promise<FeedbackStakeholder[]> {
    const params = userId ? { user_id: userId } : {}
    const response = await apiClient.get<FeedbackStakeholder[]>('/feedback/stakeholders/completed', { params })
    return response.data
  },

  /**
   * Get feedback response by ID
   */
  async getFeedbackResponse(responseId: string): Promise<any> {
    const response = await apiClient.get(`/feedback/responses/${responseId}`)
    return response.data
  },

  // ==================== #31 Explicit "Add to…" integration ====================
  // The auto-integration of feedback into the Impact Portfolio is gone.
  // Nothing lands in the portfolio/IDP unless the user explicitly clicks Add.
  // All add-* endpoints are idempotent and return already_added when the
  // entry exists, so the UI can pre-check via portfolio-status or simply POST.

  /**
   * #31 Detect whether a feedback response was already added to the
   * team member's Impact Portfolio and/or their IDP. Drives "Added ✓"
   * button state.
   */
  async getResponsePortfolioStatus(responseId: string): Promise<FeedbackResponseIntegrationStatus> {
    const response = await apiClient.get<FeedbackResponseIntegrationStatus>(
      `/feedback/responses/${responseId}/portfolio-status`
    )
    return response.data
  },

  /**
   * #31 Add a feedback response to the team member's Impact Portfolio as a
   * confidential Evidence entry. Idempotent.
   */
  async addResponseToPortfolio(responseId: string): Promise<AddToPortfolioResult> {
    const response = await apiClient.post<AddToPortfolioResult>(
      `/feedback/responses/${responseId}/add-to-portfolio`
    )
    return response.data
  },

  /**
   * June 2026: whether a feedback REQUEST is mirrored into the subject's Impact
   * Portfolio (one entry per request). Drives the People Leader's
   * "Add to … Impact Portfolio" button state.
   */
  async getRequestPortfolioStatus(requestId: string): Promise<{ in_portfolio: boolean; evidence_id: string | null }> {
    const response = await apiClient.get<{ in_portfolio: boolean; evidence_id: string | null }>(
      `/feedback/requests/${requestId}/portfolio-status`
    )
    return response.data
  },

  /**
   * June 2026 People Leader action: add a PL-initiated feedback request to the
   * team member's Impact Portfolio as a single read-only entry. Idempotent.
   */
  async addRequestToPortfolio(requestId: string): Promise<{ already_added: boolean; evidence_id: string }> {
    const response = await apiClient.post<{ already_added: boolean; evidence_id: string }>(
      `/feedback/requests/${requestId}/add-to-portfolio`
    )
    return response.data
  },

  /**
   * #31 Link a feedback response into an IDP as a strength/growth note.
   * Idempotent per (response, IDP).
   */
  async addResponseToIdp(
    responseId: string,
    idpId: string,
    noteType: 'strength' | 'growth' = 'strength'
  ): Promise<AddToIdpResult> {
    const response = await apiClient.post<AddToIdpResult>(
      `/feedback/responses/${responseId}/add-to-idp`,
      null,
      { params: { idp_id: idpId, note_type: noteType } }
    )
    return response.data
  },

  /**
   * #31 (PL side) File a feedback response into the People Leader's private
   * observations about a team member. Idempotent.
   */
  async addResponseToPlObservations(
    responseId: string,
    subjectUserId: string
  ): Promise<AddToPortfolioResult> {
    const response = await apiClient.post<AddToPortfolioResult>(
      `/feedback/responses/${responseId}/add-to-pl-observations`,
      null,
      { params: { subject_user_id: subjectUserId } }
    )
    return response.data
  },

  // ==================== Question Template Operations ====================

  /**
   * Create question template (HR admin only)
   */
  async createQuestionTemplate(data: Omit<FeedbackQuestionTemplate, 'id' | 'usage_count'>): Promise<FeedbackQuestionTemplate> {
    const response = await apiClient.post<FeedbackQuestionTemplate>('/feedback/question-templates', data)
    return response.data
  },

  /**
   * List question templates
   */
  async listQuestionTemplates(params?: {
    category?: string
    active_only?: boolean
  }): Promise<FeedbackQuestionTemplate[]> {
    const response = await apiClient.get<FeedbackQuestionTemplate[]>('/feedback/question-templates', { params })
    return response.data
  },

  /**
   * Get question template details
   */
  async getQuestionTemplate(templateId: string): Promise<FeedbackQuestionTemplate> {
    const response = await apiClient.get<FeedbackQuestionTemplate>(`/feedback/question-templates/${templateId}`)
    return response.data
  },

  // ==================== Anonymous Feedback Status ====================

  /**
   * Check if anonymous feedback has been submitted for a review
   * Used to enforce blocking requirement (#627)
   */
  async checkAnonymousFeedbackStatus(reviewSessionId: string): Promise<{
    has_request: boolean
    is_required: boolean
    is_complete: boolean
    responses_received?: number
    total_stakeholders?: number
    request_id?: string
    message: string
  }> {
    const response = await apiClient.get<{
      has_request: boolean
      is_required: boolean
      is_complete: boolean
      responses_received?: number
      total_stakeholders?: number
      request_id?: string
      message: string
    }>('/feedback/anonymous/status', {
      params: { review_session_id: reviewSessionId }
    })
    return response.data
  },

  // ==================== Statistics ====================

  /**
   * Get feedback statistics for current user
   */
  async getFeedbackStatistics(): Promise<FeedbackStatistics> {
    const response = await apiClient.get<FeedbackStatistics>('/feedback/statistics')
    return response.data
  },

  // ==================== Holistic Analysis ====================

  /**
   * Get holistic AI-powered feedback analysis for an employee
   * Aggregates all completed feedback and provides comprehensive insights
   * 
   * @param userId - Employee ID to analyze feedback for
   * @returns Comprehensive feedback analysis with themes, strengths, and recommendations
   */
  async getHolisticFeedbackAnalysis(userId: string, params?: { employee_only?: boolean; manager_only?: boolean }): Promise<HolisticFeedbackAnalysis> {
    const response = await apiClient.get<HolisticFeedbackAnalysis>(`/feedback/holistic-analysis/${userId}`, { params })
    return response.data
  },

  // ==================== Admin Operations ====================

  /**
   * List all feedback requests across the organisation (admin/HRBP access)
   */
  async listAllFeedbackRequestsAdmin(params?: {
    request_type?: FeedbackRequestType
    status?: FeedbackRequestStatus
    search?: string
    skip?: number
    limit?: number
  }): Promise<FeedbackRequest[]> {
    const response = await apiClient.get<FeedbackRequest[]>('/feedback/admin/requests', { params })
    return response.data
  }
}

// ==================== Holistic Feedback Analysis Types ====================

export interface FeedbackTheme {
  theme: string
  description: string
  frequency: number
  sentiment: 'positive' | 'neutral' | 'negative'
  examples: string[]
}

// Structured theme from holistic analysis
export interface StrengthTheme {
  theme: string
  description: string
  evidence_count?: number
  sentiment?: 'positive' | 'neutral' | 'negative'
}

// Structured recommendation from holistic analysis
export interface RecommendationItem {
  priority?: 'high' | 'medium' | 'low'
  action: string
  expected_impact?: string
  timeframe?: string
}

export interface HolisticFeedbackAnalysis {
  employee_id: string
  total_feedback_cycles: number
  total_responses: number
  analysis_date: string
  analysis_available: boolean
  error?: string
  analysis?: {
    overall_summary: string
    key_strengths: StrengthTheme[]
    development_areas: StrengthTheme[]
    themes: FeedbackTheme[]
    trend_analysis?: {
      direction: 'improving' | 'stable' | 'declining'
      description: string
    }
    actionable_recommendations: RecommendationItem[]
    confidence_score: number
  }
}
